import React, { useCallback, useEffect, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { PaginationComponent } from '../../components';
import PageLayout from '../../layouts/PageLayout';
import { messageLogAPI } from '../../services';

/**
 * #102134 — MÀN "LỊCH SỬ GỬI TIN NHẮN".
 *
 * Sinh ra để trả lời đúng câu hỏi của sếp: *"tôi vào bấm kích hoạt nhưng k biết nhân viên có
 * nhận được tin nhắn mật mã hay không? xem lịch sử gửi tin nhắn đó ở đâu?"*. Trước việc này
 * câu trả lời là KHÔNG CÓ CHỖ NÀO.
 *
 * ⚠️ Màn này KHÔNG hiển thị nội dung tin, vì máy chủ cố ý không lưu nội dung mật mã/OTP. Nó trả
 * lời đúng 5 câu: gửi cho AI, LOẠI tin gì, LÚC NÀO, THÀNH CÔNG hay THẤT BẠI (kèm lý do), và
 * AI/VIỆC NÀO kích hoạt.
 */

const TYPE_LABELS = {
  STAFF_PASSCODE: 'Mật mã NV mới',
  STAFF_PASSCODE_RESEND: 'Gửi lại mật mã NV',
  APPLICANT_REJECTION: 'Báo từ chối hồ sơ',
  APPLICANT_OTP: 'OTP ứng tuyển',
  BOOKING_OTP: 'OTP đặt bàn',
  PAYMENT_OTP: 'OTP thanh toán',
  SUPPLIER_ORDER: 'Đơn nhà cung cấp',
  OTHER: 'Khác',
};

const STATUS_META = {
  SUCCESS: { label: 'Thành công', cls: 'bg-success' },
  FAILED: { label: 'THẤT BẠI', cls: 'bg-danger' },
  // Môi trường chưa cấu hình Zalo: KHÔNG có tin thật nào ra số điện thoại. Phải tách khỏi
  // "Thành công", nếu không người xem tưởng khách đã nhận được tin.
  SKIPPED: { label: 'Không gửi (chưa cấu hình)', cls: 'bg-secondary' },
};

const CHANNEL_LABELS = { ZALO_PERSONAL: 'Zalo', TELEGRAM: 'Telegram' };

const todayVN = () => {
  // Ngày theo giờ VN, khớp với cách máy chủ diễn giải `from`/`to`.
  const d = new Date(Date.now() + 7 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
};

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh' });
  } catch {
    return iso;
  }
};

const LIMIT = 20;

export default function MessageLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [messageType, setMessageType] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(todayVN());
  const [to, setTo] = useState(todayVN());

  const params = useCallback(
    () => ({
      ...(messageType ? { message_type: messageType } : {}),
      ...(status ? { status } : {}),
      ...(search.trim() ? { search: search.trim() } : {}),
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
    }),
    [messageType, status, search, from, to],
  );

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [res, sum] = await Promise.all([
        messageLogAPI.getLogs({ ...params(), page, limit: LIMIT }),
        messageLogAPI.getSummary(params()),
      ]);
      setRows(res?.data || []);
      setTotal(res?.meta?.total || 0);
      setSummary(sum || null);
    } catch (e) {
      console.error('Error fetching message logs:', e);
      // Tải hỏng thì nói thẳng. Bảng trống im lặng ở đúng màn hình này rất nguy hiểm: người xem
      // sẽ kết luận "hệ thống chưa gửi tin nào" trong khi thật ra là chưa tải được dữ liệu.
      setError(e?.message || 'Không tải được lịch sử gửi tin nhắn.');
      setRows([]);
      setTotal(0);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [params, page]);

  useEffect(() => {
    const t = setTimeout(fetchLogs, 350);
    return () => clearTimeout(t);
  }, [fetchLogs]);

  // Đổi bộ lọc thì phải về trang 1, nếu không người dùng đứng ở trang 5 của kết quả cũ và thấy trống.
  useEffect(() => {
    setPage(1);
  }, [messageType, status, search, from, to]);

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">Lịch sử gửi tin nhắn</h3>
            </div>
            <p className="text-muted mb-0">
              Ghi vết mọi tin hệ thống gửi ra số điện thoại (mật mã nhân viên, OTP ứng tuyển, OTP
              đặt bàn, OTP thanh toán, đơn nhà cung cấp).{' '}
              <strong>Không lưu và không hiển thị nội dung mật mã/OTP</strong> — chỉ loại tin và
              kết quả gửi.
            </p>
          </div>
        </Col>

        <Col xl={12}>
          <div className="mc-card">
            <Row className="g-2 align-items-end mb-3">
              <Col xs={12} md={3}>
                <label className="form-label small mb-1">Loại tin</label>
                <select
                  className="form-select form-select-sm"
                  value={messageType}
                  onChange={(e) => setMessageType(e.target.value)}
                >
                  <option value="">Tất cả</option>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={12} md={2}>
                <label className="form-label small mb-1">Kết quả</label>
                <select
                  className="form-select form-select-sm"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Tất cả</option>
                  {Object.entries(STATUS_META).map(([v, m]) => (
                    <option key={v} value={v}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </Col>
              <Col xs={6} md={2}>
                <label className="form-label small mb-1">Từ ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </Col>
              <Col xs={6} md={2}>
                <label className="form-label small mb-1">Đến ngày</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Col>
              <Col xs={12} md={3}>
                <label className="form-label small mb-1">Tìm SĐT / tên người nhận</label>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="vd 0334313849"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
            </Row>

            {summary && (
              <div className="d-flex flex-wrap gap-3 mb-3 small">
                <span>
                  <span className="badge bg-success me-1">{summary.SUCCESS ?? 0}</span> thành công
                </span>
                <span>
                  <span className="badge bg-danger me-1">{summary.FAILED ?? 0}</span> thất bại
                </span>
                <span>
                  <span className="badge bg-secondary me-1">{summary.SKIPPED ?? 0}</span> không gửi
                </span>
              </div>
            )}

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/*
              #102134 — ĐO TRÊN ĐIỆN THOẠI TRƯỚC (390x844) mới lộ ra: bảng 7 cột bắt cuộn ngang,
              mà cột bị đẩy khuất lại đúng là cột QUAN TRỌNG NHẤT — "Kết quả" và "Lý do lỗi".
              Người mở màn này trên điện thoại là để biết tin GỬI ĐƯỢC HAY KHÔNG; bắt họ cuộn
              ngang mới thấy là hỏng đúng mục đích của màn hình. Nên trên máy nhỏ dùng thẻ dọc,
              từ md trở lên mới dùng bảng.
            */}
            <div className="d-md-none">
              {loading && rows.length === 0 ? (
                <p className="text-center py-4 text-muted">Đang tải…</p>
              ) : rows.length === 0 && !error ? (
                <p className="text-center py-4 text-muted">
                  Không có tin nào trong khoảng đã chọn.
                </p>
              ) : (
                rows.map((r) => {
                  const meta = STATUS_META[r.status] || { label: r.status, cls: 'bg-secondary' };
                  return (
                    <div key={r.id} className="border rounded p-2 mb-2">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-medium">
                            {r.recipient_name || r.recipient_employee?.full_name || '(không rõ tên)'}
                          </div>
                          <div className="small text-muted">{r.recipient_phone}</div>
                        </div>
                        <span className={`badge ${meta.cls} flex-shrink-0`}>{meta.label}</span>
                      </div>
                      <div className="small mt-1">
                        {TYPE_LABELS[r.message_type] || r.message_type} ·{' '}
                        {CHANNEL_LABELS[r.channel] || r.channel} · {fmtTime(r.created_at)}
                      </div>
                      {r.error_message && (
                        <div className="small text-danger mt-1">Lỗi: {r.error_message}</div>
                      )}
                      <div className="small text-muted mt-1">
                        Kích hoạt bởi: {r.triggered_by?.full_name || r.triggered_by_label || '—'}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="mc-table-responsive d-none d-md-block">
              <table className="mc-table">
                <thead className="mc-table-head primary">
                  <tr>
                    <th>Thời gian</th>
                    <th>Người nhận</th>
                    <th>Loại tin</th>
                    <th>Kênh</th>
                    <th>Kết quả</th>
                    <th>Lý do lỗi</th>
                    <th>Ai kích hoạt</th>
                  </tr>
                </thead>
                <tbody className="mc-table-body even">
                  {loading && rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Đang tải…
                      </td>
                    </tr>
                  ) : rows.length === 0 && !error ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Không có tin nào trong khoảng đã chọn.
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => {
                      const meta = STATUS_META[r.status] || {
                        label: r.status,
                        cls: 'bg-secondary',
                      };
                      return (
                        <tr key={r.id}>
                          <td className="small text-nowrap">{fmtTime(r.created_at)}</td>
                          <td>
                            <div className="fw-medium">
                              {r.recipient_name ||
                                r.recipient_employee?.full_name ||
                                '(không rõ tên)'}
                            </div>
                            <div className="small text-muted">{r.recipient_phone}</div>
                          </td>
                          <td className="small">{TYPE_LABELS[r.message_type] || r.message_type}</td>
                          <td className="small">{CHANNEL_LABELS[r.channel] || r.channel}</td>
                          <td>
                            <span className={`badge ${meta.cls}`}>{meta.label}</span>
                          </td>
                          <td className="small text-danger">{r.error_message || ''}</td>
                          <td className="small">
                            {r.triggered_by?.full_name || r.triggered_by_label || '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {total > LIMIT && (
              <PaginationComponent
                currentPage={page}
                totalItems={total}
                itemsPerPage={LIMIT}
                onPageChange={setPage}
              />
            )}
          </div>
        </Col>
      </Row>
    </PageLayout>
  );
}
